import { ReactNode, useState } from 'react';
import { Check, Table2, Trash2, BarChart3 } from 'lucide-react';
import type { Decisao } from './squad-metrics';

export interface TabelaEquivalente {
  colunas: string[];
  linhas: Array<Array<string | number>>;
}

interface PainelCardProps {
  id: string;
  titulo: string;
  /** A pergunta que este painel responde na conversa de reestruturação. */
  pergunta: string;
  decisao: Decisao;
  onDecisao: (id: string, decisao: Decisao) => void;
  tabela: TabelaEquivalente;
  children: ReactNode;
  className?: string;
}

/**
 * Moldura dos painéis. Faz três coisas além de emoldurar:
 *  - carrega a tabela equivalente ao gráfico (todo valor legível sem depender de cor);
 *  - registra a decisão do gestor: este painel fica ou sai;
 *  - marca visualmente o que já foi descartado, sem esconder da tela.
 */
export function PainelCard({
  id,
  titulo,
  pergunta,
  decisao,
  onDecisao,
  tabela,
  children,
  className = '',
}: PainelCardProps) {
  const [verTabela, setVerTabela] = useState(false);
  const cortado = decisao === 'cortar';

  return (
    <figure
      className={`bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-sm border-2 transition-colors ${
        decisao === 'manter'
          ? 'border-schmersal-turquoise'
          : 'border-schmersal-gray-light dark:border-schmersal-gray-dark'
      } ${className}`}
    >
      <figcaption className="flex flex-col gap-3 p-5 border-b border-schmersal-gray-light dark:border-schmersal-gray-dark sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h3 className="font-semibold text-schmersal-gray-dark dark:text-white">{titulo}</h3>
          <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light mt-0.5">{pergunta}</p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setVerTabela((v) => !v)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md border border-schmersal-gray-light dark:border-schmersal-gray-dark text-schmersal-gray-dark dark:text-white hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark transition-colors"
            aria-pressed={verTabela}
          >
            {verTabela ? <BarChart3 className="h-3.5 w-3.5" /> : <Table2 className="h-3.5 w-3.5" />}
            {verTabela ? 'Gráfico' : 'Tabela'}
          </button>

          <div className="flex items-center rounded-md border border-schmersal-gray-light dark:border-schmersal-gray-dark overflow-hidden">
            <button
              type="button"
              onClick={() => onDecisao(id, decisao === 'manter' ? 'indefinido' : 'manter')}
              className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs transition-colors ${
                decisao === 'manter'
                  ? 'bg-schmersal-turquoise text-white'
                  : 'text-schmersal-gray-dark dark:text-white hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark'
              }`}
              aria-pressed={decisao === 'manter'}
              title="Manter este painel no sistema"
            >
              <Check className="h-3.5 w-3.5" />
              Manter
            </button>
            <button
              type="button"
              onClick={() => onDecisao(id, cortado ? 'indefinido' : 'cortar')}
              className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs transition-colors border-l border-schmersal-gray-light dark:border-schmersal-gray-dark ${
                cortado
                  ? 'bg-schmersal-gray-medium text-white'
                  : 'text-schmersal-gray-dark dark:text-white hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark'
              }`}
              aria-pressed={cortado}
              title="Descartar este painel"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Cortar
            </button>
          </div>
        </div>
      </figcaption>

      <div className={`p-5 ${cortado ? 'opacity-40' : ''}`}>
        {verTabela ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
                  {tabela.colunas.map((coluna) => (
                    <th
                      key={coluna}
                      className="py-2 px-2 font-semibold text-schmersal-gray-dark dark:text-white whitespace-nowrap"
                    >
                      {coluna}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tabela.linhas.map((linha, i) => (
                  <tr key={i} className="border-b border-schmersal-gray-light dark:border-schmersal-gray-dark last:border-0">
                    {linha.map((celula, j) => (
                      <td
                        key={j}
                        className={`py-2 px-2 text-schmersal-gray-dark dark:text-white whitespace-nowrap ${
                          j === 0 ? 'font-medium' : 'tabular-nums'
                        }`}
                      >
                        {celula}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          children
        )}
      </div>
    </figure>
  );
}
